//src/utils/opaque-login.utils.ts

import { KE2, type OpaqueClient } from '@cloudflare/opaque-ts';

import { OPAQUE_CONFIG, SERVER_IDENTITY } from '@/lib/opaque-config';
import { createLogger } from '@/utils/logger';

import { bytesToHex, hexToBytes, unwrapResult, createOpaqueClient, EMPTY_LENGTH } from './opaque-utils';

const log = createLogger({ namespace: 'six3ro:auth:opaque' });

/**
 * Result of OPAQUE authentication initialization
 */
export interface OpaqueAuthInit {
  ke1: string;
  client: OpaqueClient;
}

/**
 * Result of OPAQUE authentication finalization
 */
export interface OpaqueAuthFinish {
  ke3: string;
  clientSessionKey: string;
}

/**
 * Creates an OPAQUE authentication initialization (KE1)
 *
 * This is Step 1 of the OPAQUE login flow:
 * 1. Client generates KE1 from password
 * 2. KE1 is sent to server for authentication
 *
 * @param password - User's password
 * @returns KE1 as hex string and the client instance (must be reused for authFinish)
 * @throws Error if KE1 creation fails
 */
export async function createAuthInit(password: string): Promise<OpaqueAuthInit> {
  log.debug('createAuthInit START');
  log.debug('Password length', { length: password.length });

  const client = createOpaqueClient();
  log.debug('OpaqueClient created', { client });

  // Generate KE1
  const ke1Result = await client.authInit(password);
  log.debug('ke1Result', { ke1Result });
  const ke1 = unwrapResult(ke1Result, 'Failed to create KE1');

  const ke1Hex = bytesToHex(ke1.serialize());
  log.debug('ke1 serialized', { hex: ke1Hex, charLength: ke1Hex.length, byteLength: ke1Hex.length / 2 });
  log.debug('createAuthInit END');

  return {
    ke1: ke1Hex,
    client, // Return client for reuse in finalizeAuth
  };
}

/**
 * Finalizes OPAQUE authentication by processing server's KE2
 *
 * This is Step 2 of the OPAQUE login flow:
 * 1. Receives server's KE2 response
 * 2. Processes KE2 and generates KE3
 * 3. Derives session key
 *
 * @param client - The OPAQUE client instance from createAuthInit (must be reused)
 * @param ke2Hex - Server's KE2 response (hex string)
 * @param username - User's username (used for verification)
 * @returns KE3 and client session key for verification
 * @throws Error if authentication fails or credentials are invalid
 */
export async function finalizeAuth(
  client: OpaqueClient,
  ke2Hex: string,
  username: string
): Promise<OpaqueAuthFinish> {
  log.debug('finalizeAuth START');
  log.debug('Input ke2Hex', { ke2Hex, charLength: ke2Hex.length, byteLength: ke2Hex.length / 2 });
  log.debug('Auth context', { username, serverIdentity: SERVER_IDENTITY, config: OPAQUE_CONFIG });

  // Deserialize server's KE2
  const ke2Bytes = hexToBytes(ke2Hex);
  log.debug('ke2Bytes', { length: ke2Bytes.length, first50: ke2Bytes.slice(0, 50) });

  let ke2Msg;
  try {
    ke2Msg = KE2.deserialize(OPAQUE_CONFIG, ke2Bytes);
    log.debug('KE2.deserialize SUCCESS', { ke2Msg });
  } catch (deserializeError) {
    log.error('KE2.deserialize FAILED', deserializeError);
    throw deserializeError;
  }

  // Process KE2 and generate KE3
  try {
    log.debug('Calling client.authFinish', { clientState: client });

    // Access internal state to debug
    const clientAny = client as any;
    log.debug('Client internal state', {
      passwordLength: clientAny.password?.length,
      blind: clientAny.blind,
      ke1: clientAny.ke1,
    });

    // Try to manually call recoverCredentials to see what comes out
    log.debug('Attempting manual recoverCredentials');
    const te = new TextEncoder();
    const serverIdentityU8 = te.encode(SERVER_IDENTITY);
    const clientIdentityU8 = te.encode(username);

    try {
      const recResult = await clientAny.opaque_core.recoverCredentials(
        clientAny.password,
        clientAny.blind,
        ke2Msg.response,
        serverIdentityU8,
        clientIdentityU8
      );
      log.debug('recoverCredentials result', { recResult });
      if (recResult && !(recResult instanceof Error)) {
        log.debug('recoverCredentials details', {
          clientAkePublicKey: recResult.client_ake_keypair?.public_key,
          clientAkePublicKeyLength: recResult.client_ake_keypair?.public_key?.length,
          clientAkePrivateKeyLength: recResult.client_ake_keypair?.private_key?.length,
          serverPublicKey: recResult.server_public_key,
          serverPublicKeyLength: recResult.server_public_key?.length,
          serverPublicKeyHex: recResult.server_public_key ? bytesToHex(recResult.server_public_key) : 'N/A',
        });
      }
    } catch (recoverError) {
      log.error('recoverCredentials threw', recoverError);
    }

    const finishRes = await client.authFinish(ke2Msg, SERVER_IDENTITY, username);
    log.debug('authFinish raw result', { finishRes });
    const { ke3, session_key: clientSessionKeyBytes } = unwrapResult(
      finishRes,
      'Invalid username or password'
    );

    log.debug('finalizeAuth SUCCESS');
    return {
      ke3: bytesToHex(ke3.serialize()),
      clientSessionKey: bytesToHex(clientSessionKeyBytes),
    };
  } catch (error) {
    log.error('authFinish FAILED', error);
    log.error('Error stack', { stack: error instanceof Error ? error.stack : 'no stack' });
    log.debug('finalizeAuth END (ERROR)');
    throw new Error('Invalid username or password');
  }
}

/**
 * Complete OPAQUE authentication flow helper
 *
 * Combines both steps of OPAQUE authentication:
 * 1. Creates KE1 from password
 * 2. Processes server's KE2 and verifies session key
 *
 * Note: This function is NOT used directly because the client instance
 * must persist between createAuthInit and finalizeAuth calls.
 * Use createAuthInit and finalizeAuth separately instead.
 *
 * @param client - The OPAQUE client instance from createAuthInit
 * @param username - User's username
 * @param serverResponse - Server's response containing sessionId and KE2
 * @returns Object with sessionId, KE3, and client session key for final verification
 * @throws Error if authentication fails or session key doesn't match
 */
export async function performOpaqueAuth(
  client: OpaqueClient,
  username: string,
  serverResponse: { sessionId: string; ke2: string }
): Promise<{ sessionId: string; ke3: string; clientSessionKey: string }> {
  const { sessionId, ke2 } = serverResponse;

  // Validate server response
  if (sessionId.length === EMPTY_LENGTH || ke2.length === EMPTY_LENGTH) {
    throw new Error('Invalid server response: missing sessionId or ke2');
  }

  // Finalize authentication with the same client instance
  const { ke3, clientSessionKey } = await finalizeAuth(client, ke2, username);

  return {
    sessionId,
    ke3,
    clientSessionKey,
  };
}
